/*
  In the Path Commands lesson, I have demos that show a syntax-highlighted code snippet next to a Bézier curve with draggable handles. While the user drags one of the handles, I want to highlight that part of the path instruction: The relevant Q/C command turns yellow, while the rest of the code turns grey.

  This isn't something I can do with StaticCodeSnippet, so instead I’ll fake it.
*/

import { styled } from '@linaria/react';

interface Point {
  x: number;
  y: number;
}

interface Props {
  variant: 'quadratic' | 'cubic';
  points: Array<Point>;
  activePointIndex: number | null;
}

function PathsPseudoCodeSnippet({
  variant,
  points,
  activePointIndex,
}: Props) {
  const [, , ...finalPoints] = points;

  const letter = variant === 'quadratic' ? 'Q' : 'C';

  return (
    <Wrapper data-has-active-point={activePointIndex !== null}>
      <div>
        <Pink>{`<svg`}</Pink> <Purple>{`viewBox=`}</Purple>
        <Gray>{`"0 0 16 16"`}</Gray>
        <Pink>{`>`}</Pink>
      </div>
      <div>
        {'  '}
        <Pink>{`<path`}</Pink>
      </div>
      <div>
        {'    '}
        <Purple>{`d=`}</Purple>
        <Gray>"</Gray>
      </div>
      <div>
        {'      '}

        <Point
          data-is-active={activePointIndex === 0}
        >{`M ${points[0].x},${points[0].y}`}</Point>
      </div>
      <div>
        {'      '}
        <Point
          data-is-active={activePointIndex === 1}
        >{`${letter} ${points[1].x},${points[1].y}`}</Point>
      </div>
      {finalPoints.map((point, index) => (
        <div key={index}>
          {'      '}
          <Point
            data-is-active={activePointIndex === index + 2}
          >{`  ${point.x},${point.y}`}</Point>
        </div>
      ))}
      <div>
        {'    '}
        <Gray>{`"`}</Gray>
      </div>
      <div>
        {'  '}
        <Pink>{`/>`}</Pink>
      </div>
      <Pink>{`</svg>`}</Pink>
    </Wrapper>
  );
}

const Wrapper = styled.pre`
  font-size: 0.925rem;
  font-family: var(--font-family-mono);
  font-weight: var(--font-weight-medium);
  white-space: pre;
  padding: 16px;
`;

const Color = styled.span`
  transition:
    opacity 200ms,
    color 200ms;

  ${Wrapper}[data-has-active-point='true'] & {
    color: var(--color-text);
    opacity: 0.5;
  }
`;

const Pink = styled(Color)`
  color: var(--syntax-prop);
`;

const Purple = styled(Color)`
  color: var(--syntax-name);
`;

const Gray = styled(Color)`
  color: var(--syntax-val);
`;

const Point = styled(Gray)`
  &[data-is-active='true'] {
    color: var(--wham-primary-500) !important;
    opacity: 1 !important;
  }
`;

export default PathsPseudoCodeSnippet;
